import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { NgFor, NgIf } from "@angular/common";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from "@angular/material/card";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import {
  IFlatStyle,
  IRulesStyle,
  IStyleExpression,
  IStyleLegendEntry,
  IStylePropertyValue,
  IStyleRule,
  IVectorStyle,
} from "../../shared/model/vector-style-definition.interface";
import { IConditionLeaf, OPERATORS } from "../../shared/model/vector-style-condition.model";
import { filterToCondition, unwrapGetExpression } from "../../shared/utils/vector-style-condition.util";
import { resolveIconKeyFromDataUri } from "../../shared/utils/svg-icon-data-uri.util";
import { SVG_ICONS_DICTIONARY } from "../../shared/icons/svg-icons-dictionary";
import { StyleLegendService } from "../../shared/services/style-legend.service";
import { VIEWER_SERVICE } from "../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";

const OPERATOR_SYMBOLS: Readonly<Record<string, string>> = Object.fromEntries(OPERATORS.map((op) => [op.value, op.symbol]));

/** The stretch of values of a numeric property a ramp row stands for: `min` up to `max` (`max` included in the last one). */
interface ILegendBand {
  property: string;
  min: number;
  max: number;
  maxInclusive: boolean;
}

/** One rendered row of the legend: a color swatch plus the human-readable rule it represents. */
interface ILegendRow {
  /** Unique id of the row (its position in the legend). Passed back on click to toggle it. Equal to `index` for rows built from the rules. */
  id: number;
  /** Position in the source style's rules array of the rule the row comes from; for a flat style, always 0. */
  index: number;
  /** Set on the rows of a ramp: hiding one hides only the features whose value is in the band, not the whole rule. */
  band?: ILegendBand;
  /** True when this row is currently rendered on the map, false when the user has toggled it off in the legend. */
  isVisible: boolean;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  label: string;
  /** Set when the rule's style has an icon — the swatch is rendered as this dictionary icon instead of a plain color. */
  iconKey?: string;
  /** Set when the rule's style has a text label — rendered as a "Label: {property}" hint under the row. */
  labelProperty?: string;
}

const DEFAULT_SWATCH_FILL = "rgba(150,150,150,0.4)";
const DEFAULT_SWATCH_STROKE = "#999";

/** Fully-transparent style properties applied in place of a toggled-off rule's own style. */
const TRANSPARENT_PROPERTIES: Record<string, IStylePropertyValue> = {
  "fill-color": "rgba(0,0,0,0)",
  "stroke-color": "rgba(0,0,0,0)",
  "stroke-width": 0,
  "circle-radius": 0,
  "circle-fill-color": "rgba(0,0,0,0)",
  "circle-stroke-color": "rgba(0,0,0,0)",
};

/** The rule's style with the features of each band drawn transparent: the rule keeps matching them, so no later rule or `else` repaints them. */
function hideBands(style: Record<string, IStylePropertyValue>, bands: readonly ILegendBand[]): Record<string, IStylePropertyValue> {
  const conditions: IStyleExpression[] = bands.map((band) => [
    "all",
    [">=", ["get", band.property], band.min],
    [band.maxInclusive ? "<=" : "<", ["get", band.property], band.max],
  ]);
  const inHiddenBand: IStyleExpression = conditions.length === 1 ? conditions[0] : ["any", ...conditions];
  const hidden: Record<string, IStylePropertyValue> = { ...style };
  Object.entries(TRANSPARENT_PROPERTIES).forEach(([key, transparent]) => {
    if (key in style) hidden[key] = ["case", inHiddenBand, transparent, style[key]];
  });
  return hidden;
}

/**
 * Returns a new IVectorStyle with what the legend has toggled off hidden: a rule whose own row is hidden gets transparent
 * props; the hidden rows of a ramp hide only their band of values in that rule.
 */
function buildEffectiveStyle(original: IVectorStyle, rows: readonly ILegendRow[], hiddenRowIds: readonly number[]): IVectorStyle {
  if (hiddenRowIds.length === 0) return original;

  const hiddenIds = new Set(hiddenRowIds);
  const hiddenRows = rows.filter((row) => hiddenIds.has(row.id));

  if (original.type === "flat") {
    if (hiddenRows.some((row) => row.index === 0)) {
      return { type: "flat", properties: TRANSPARENT_PROPERTIES } satisfies IFlatStyle;
    }
    return original;
  }

  const rules: IStyleRule[] = original.rules.map((rule, i) => {
    const ofRule = hiddenRows.filter((row) => row.index === i);
    if (ofRule.some((row) => !row.band)) return { ...rule, style: TRANSPARENT_PROPERTIES };
    const bands = ofRule.flatMap((row) => (row.band ? [row.band] : []));
    return bands.length > 0 ? { ...rule, style: hideBands(rule.style, bands) } : rule;
  });
  return { type: "rules", rules } satisfies IRulesStyle;
}

function swatchFromProps(style: Record<string, unknown> | undefined): { fillColor: string; strokeColor: string; strokeWidth: number } {
  return {
    fillColor: (style?.["fill-color"] as string) ?? DEFAULT_SWATCH_FILL,
    strokeColor: (style?.["stroke-color"] as string) ?? DEFAULT_SWATCH_STROKE,
    strokeWidth: (style?.["stroke-width"] as number) ?? 1,
  };
}

function describeLeaf(leaf: IConditionLeaf): string {
  const symbol = OPERATOR_SYMBOLS[leaf.operator] ?? leaf.operator;
  const value = Array.isArray(leaf.value) ? `[${leaf.value.join(", ")}]` : String(leaf.value);
  return `${leaf.propertyPath.join(".")} ${symbol} ${value}`;
}

/** Turns an OpenLayers-style filter expression into a short human-readable condition, e.g. "area > 2.508" or "a > 1 AND b < 2". */
function describeFilter(filter: unknown, propertyHint?: string): string {
  if (filter === undefined) return propertyHint ?? "Match";

  const condition = filterToCondition(filter as unknown[]);
  if (condition.unsupported) return "Custom filter";
  if (condition.conditions.length === 0) return propertyHint ?? "Match";
  if (condition.conditions.length === 1) return describeLeaf(condition.conditions[0]);

  const joiner = condition.combinator === "any" ? " OR " : " AND ";
  return condition.conditions.map(describeLeaf).join(joiner);
}

function buildRow(style: Record<string, unknown> | undefined, label: string, index: number, id: number, isVisible: boolean): ILegendRow {
  return {
    id,
    index,
    isVisible,
    ...swatchFromProps(style),
    label,
    iconKey: resolveIconKeyFromDataUri(style?.["icon-src"], SVG_ICONS_DICTIONARY),
    labelProperty: unwrapGetExpression(style?.["text-value"])?.join("."),
  };
}

function bandOf(entry: IStyleLegendEntry): ILegendBand | undefined {
  return entry.property && entry.min !== undefined && entry.max !== undefined
    ? { property: entry.property, min: entry.min, max: entry.max, maxInclusive: entry.max_inclusive === true }
    : undefined;
}

/**
 * Rows from the legend the style's metadata carries (computed from its rules): one per entry, so a ramp shows a row per
 * stretch between stops, with a gradient swatch. Each row keeps the stroke / icon / label of the rule it comes from. A row
 * with a band hides only its values; any other row hides its whole rule.
 * `undefined` when the style has no usable legend (none, or one that points at a rule the style does not have), so the
 * rows are built from the rules.
 */
function buildRowsFromLegend(
  styleDefinition: IVectorStyle,
  legend: readonly IStyleLegendEntry[] | undefined,
  hidden: ReadonlySet<number>,
): ILegendRow[] | undefined {
  if (!legend?.length) return undefined;
  const ruleCount = styleDefinition.type === "flat" ? 1 : styleDefinition.rules.length;
  if (legend.some((entry, i) => (entry.rule_index ?? i) >= ruleCount)) return undefined;

  // The rows of a rule that have no band (its own row, or the categories of a `match`) hide that whole rule together.
  const hiddenRules = new Set(legend.flatMap((entry, id) => (!bandOf(entry) && hidden.has(id) ? [entry.rule_index ?? id] : [])));
  return legend.map((entry, id) => {
    const index = entry.rule_index ?? id;
    const band = bandOf(entry);
    const style = styleDefinition.type === "flat" ? styleDefinition.properties : styleDefinition.rules[index].style;
    const fillColor = entry.color_to ? `linear-gradient(90deg, ${entry.color}, ${entry.color_to})` : entry.color;
    const isVisible = band ? !hidden.has(id) : !hiddenRules.has(index);
    return { ...buildRow(style, entry.label, index, id, isVisible), fillColor, band };
  });
}

function buildLegendRows(
  styleDefinition: IVectorStyle,
  hiddenRowIds: readonly number[],
  propertyHint?: string,
  legend?: readonly IStyleLegendEntry[],
): ILegendRow[] {
  const hidden = new Set(hiddenRowIds);
  const fromLegend = buildRowsFromLegend(styleDefinition, legend, hidden);
  if (fromLegend) return fromLegend;
  if (styleDefinition.type === "flat") {
    return [buildRow(styleDefinition.properties, "All features", 0, 0, !hidden.has(0))];
  }

  return styleDefinition.rules.map((rule: IStyleRule, i) =>
    buildRow(rule.style, rule.else ? "Other" : describeFilter(rule.filter, propertyHint), i, i, !hidden.has(i)),
  );
}

/**
 * Floating, draggable legend for a layer's flat/rules `IVectorStyle` — shows a color swatch (or the
 * rule's icon, when set) and a human-readable condition per rule, plus a "Label: {property}" hint
 * when the rule renders a text label. Opened either manually (debug "Show legend" menu item) or
 * automatically after the AI `apply_layer_style` tool succeeds, via StyleLegendService.
 *
 * Clicking a row toggles the visibility of its features on the map: those of its rule, or only those in its band of values
 * for a row of a ramp. This is transient: the layer's persisted `styleDefinition` is never mutated — a "hidden" row is
 * applied by calling `BaseViewerService.applyLayerStyleTransient` with a modified style. Closing the panel restores the
 * original saved style.
 */
@Component({
  selector: "lib-style-legend-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgFor, NgIf, CdkDrag, CdkDragHandle, MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatIcon, MatIconButton, MatTooltip],
  templateUrl: "./style-legend-panel.component.html",
  styleUrl: "./style-legend-panel.component.scss",
})
export class StyleLegendPanelComponent {
  private readonly legendService = inject(StyleLegendService);
  private readonly viewerService = inject(VIEWER_SERVICE);

  readonly state = this.legendService.state;

  readonly rows = computed<ILegendRow[]>(() => {
    const current = this.state();
    return current
      ? buildLegendRows(current.styleDefinition, current.hiddenRowIds, current.metadata?.property_used, current.metadata?.legend)
      : [];
  });

  toggleRow(rowId: number): void {
    const current = this.state();
    if (!current) return;

    const clicked = this.rows().find((row) => row.id === rowId);
    if (!clicked) return;

    // A row with a band is toggled alone; a row without one stands for its whole rule, so it moves with the other rows of that rule.
    const affected = clicked.band ? [clicked.id] : this.rows().filter((row) => !row.band && row.index === clicked.index).map((row) => row.id);
    const hidden = new Set(current.hiddenRowIds);
    affected.forEach((id) => (clicked.isVisible ? hidden.add(id) : hidden.delete(id)));
    const nextHidden = Array.from(hidden).sort((a, b) => a - b);
    this.legendService.setHiddenRowIds(nextHidden);

    const layer = this.viewerService.findUserLayerById(current.layerId);
    if (!layer) return;
    const effective = buildEffectiveStyle(current.styleDefinition, this.rows(), nextHidden);
    this.viewerService.applyLayerStyleTransient(layer, effective);
  }

  close(): void {
    const current = this.state();
    if (current && current.hiddenRowIds.length > 0) {
      const layer = this.viewerService.findUserLayerById(current.layerId);
      if (layer) this.viewerService.applyLayerStyleTransient(layer, current.styleDefinition);
    }
    this.legendService.hide();
  }
}
